<template>
  <div class="w-100 d-flex flex-column justify-start align-start">
    <v-radio-group
      v-model="modelValue"
      color="brandPrimary"
      density="compact"
      hide-details
      :ripple="false"
      :disabled="exportInProgress"
      @update:model-value="handlePngModeChange"
    >
      <v-radio
        label="Full report"
        value="full-report"
      />
      <v-radio
        label="Individual widgets"
        value="individual-widgets"
      />
    </v-radio-group>
    <div
      v-if="modelValue === 'individual-widgets'"
      class="d-flex flex-column align-start justify-start ml-5"
    >
      <v-checkbox
        :model-value="allWidgetsSelected"
        label="All widgets"
        color="brandPrimary"
        hide-details
        class="my-2"
        :disabled="exportInProgress"
        @update:model-value="handleSelectAll"
      />
      <div class="d-flex flex-column align-start justify-start ml-7">
        <v-checkbox
          v-for="option in options"
          :key="option.id"
          v-model="selectedItems"
          :label="option.title"
          :value="option.id"
          color="brandPrimary"
          hide-details
          class="mb-2"
          :disabled="exportInProgress"
        />
      </div>
    </div>
    <DpplNewButton
      :disabled="buttonDisabled"
      color="brandPrimary"
      size="small"
      class="w-100 mt-4"
      @click="handlePngDownload"
    >
      {{ buttonText }}
    </DpplNewButton>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { downloadMultiplePNGs, downloadPNG } from '@/helpers/fileDownload';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useColors } from '@/composables/colors';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useInsightsExport } from '@/stores/insights/export';
import { useTracking } from '@/stores/tracking/tracking';
import { READABLE_FILE_NAME_FORMAT, toFileNameFormat } from '@/helpers/date';
import { format } from 'date-fns';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { wait } from '@/helpers/wait';

const emits = defineEmits(['close']);

const props = defineProps({
  exportConfig: {
    type: Object,
    required: true,
  },
});

const { pngWidgetOptions, pageDownloadId } = props.exportConfig || {};

const route = useRoute();
const { bgCanvas } = useColors();

const { authData } = storeToRefs(useAppAuthData());
const { dateRange } = storeToRefs(useDatePicker());
const { exportInProgress } = storeToRefs(useInsightsExport());

const {
  addErrorSnackbar,
  addSuccessSnackbar,
} = useAppSnackbar();
const { updateExportInProgress, updateFullReportExportInProgress } = useInsightsExport();

const filterDrawerStore = useFilterDrawer();

const modelValue = ref('full-report');
const selectedItems = ref([]);

const options = computed(() => Object.entries(pngWidgetOptions || {})
  .map(([id, title]) => ({ id, title })));

const allWidgetsSelected = computed(() => selectedItems.value.length === options.value.length);

const buttonDisabled = computed(() => exportInProgress.value || (modelValue.value === 'individual-widgets' && !selectedItems.value.length));

const buttonText = computed(() => {
  if (exportInProgress.value) return 'Downloading...';
  return `Download Image${modelValue.value === 'individual-widgets' && selectedItems.value.length > 1 ? 's' : ''}`;
});

const handlePngModeChange = (value) => {
  // Select all individual widgets by default
  if (value === 'individual-widgets') {
    selectedItems.value = options.value.map((item) => item.id);
  }
};

const downloadFullReport = async () => {
  updateFullReportExportInProgress(true);

  let fileName = `${authData.value.projectName}-${route.name}`;

  if (dateRange.value.from && dateRange.value.to) {
    fileName += `-${toFileNameFormat(dateRange.value.from)}-${toFileNameFormat(dateRange.value.to)}`;
  } else {
    fileName += `-${format(new Date(), READABLE_FILE_NAME_FORMAT)}`;
  }

  await downloadPNG({
    backgroundColor: bgCanvas.value,
    containerSelector: `[data-png-export=${pageDownloadId}]`,
    style: { margin: '0' },
    fileName,
  });

  if (props.exportConfig?.pngTrackingEventFn) {
    props.exportConfig.pngTrackingEventFn();
  } else {
    useTracking().insightsDownloadReport({
      reportName: route.name,
      downloadType: 'Full Report',
      downloadLocation: 'Download Modal',
      fileType: 'PNG',
    });
  }

  addSuccessSnackbar({
    text: 'Image downloaded successfully',
  });

  updateFullReportExportInProgress(false);
};

const downloadWidgets = async () => {
  const selectedOptions = options.value
    .filter((item) => selectedItems.value.includes(item.id));

  const today = format(new Date(), READABLE_FILE_NAME_FORMAT);
  const dateString = dateRange.value.from && dateRange.value.to
    ? `${toFileNameFormat(dateRange.value.from)}-${toFileNameFormat(dateRange.value.to)}`
    : `${today}`;

  const pngConfigs = selectedOptions.map((item) => ({
    backgroundColor: '#ffffff',
    containerSelector: `[data-widget-id=${item.id}]`,
    fileName: `${authData.value.projectName}-${route.name}-${item.id}-${dateString}`,
  }));

  await downloadMultiplePNGs(pngConfigs);

  selectedOptions.forEach((item) => {
    useTracking().insightsDownloadReport({
      reportName: route.name,
      downloadType: 'Individual Widgets',
      downloadLocation: 'Download Modal',
      fileType: 'PNG',
      widgetName: item.title,
    });
  });

  addSuccessSnackbar({
    text: `Image${pngConfigs.length > 1 ? 's' : ''} downloaded successfully`,
  });
};

const handlePngDownload = async () => {
  let reopenFilters = false;

  try {
    updateExportInProgress(true);

    // Close filter drawer if open to ensure full report is captured
    if (filterDrawerStore.open) {
      reopenFilters = true;

      filterDrawerStore.open = false;

      await wait(500);
    }

    if (modelValue.value === 'full-report') {
      await downloadFullReport();
    } else {
      await downloadWidgets();
    }
  } catch (error) {
    addErrorSnackbar({ text: 'Failed to download images' });
    throw error;
  } finally {
    updateExportInProgress(false);
    emits('close');

    if (reopenFilters) filterDrawerStore.open = true;
  }
};

const handleSelectAll = () => {
  selectedItems.value = allWidgetsSelected.value
    ? []
    : options.value.map((item) => item.id);
};
</script>

<style scoped lang="less">
:deep(.v-selection-control__input) {
  height: 20px;
  width: 20px;

  .v-icon {
    width: 20px;
    height: 20px;
  }

  input {
    width: 20px;
    height: 20px;
  }
}

:deep(.v-selection-control) {
  .v-selection-control__wrapper {
    height: 20px !important;
  }

  .v-label {
    font-size: 13px !important;
    font-weight: 500;
    line-height: 150%;
  }
}

:deep(.v-selection-control):not(:nth-child(1)) {
  margin-top: 16px;
}

:deep(.v-selection-control) {
  min-height: inherit !important;
}

:deep(.v-selection-control__input) > .v-icon {
  opacity: 1.0 !important;
}
</style>
